import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBars,
  FaBox,
  FaChevronDown,
  FaSearch,
  FaSignInAlt,
  FaSignOutAlt,
  FaTimes,
  FaUserCircle,
  FaUserCog,
} from "react-icons/fa";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { RxHamburgerMenu } from "react-icons/rx";
import API from "../../../utils/api";
import RenderToast from "../../RenderToast";
import SocialSection from "../../SocialSection";
import classes from "./HeaderOne.module.css";
import { IoIosArrowForward } from "react-icons/io";
import { FaUser } from "react-icons/fa";
import { GrContact } from "react-icons/gr";

import { BiSupport } from "react-icons/bi";

function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function HeaderOne() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [user, setUser] = useState(readStoredUser());
  const [dashOpen, setDashOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [openParent, setOpenParent] = useState(null);
  const [openMenus, setOpenMenus] = useState({ searchCategories: false });
  const [openAllCategories, setOpenAllCategories] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const drawerRef = useRef();
  const searchWrapRef = useRef();
  const searchCategoriesRef = useRef(null);

  const normalizedSearch = search.trim().toLowerCase();
  const uploadBase = API.base?.endsWith("/serverstock_api")
    ? API.base
    : `${API.base}/serverstock_api`;

  const getProductThumb = (imageName) =>
    imageName
      ? `${uploadBase}/upload/${imageName}`
      : `${uploadBase}/upload/placeholder-image.jpg`;

  const suggestionResults = useMemo(() => {
    if (normalizedSearch.length < 2) return [];
    return allProducts
      .filter((p) =>
        String(p.sku || "")
          .toLowerCase()
          .includes(normalizedSearch)
      )
      .slice(0, 6);
  }, [allProducts, normalizedSearch]);

  // Fetch categories
  useEffect(() => {
    setCategoriesLoading(true);
    fetch(`${API.base}/categories.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ action: "fetch" }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const parents = data
            .filter((c) => !c.parent_id && c.categoryName !== "Uncategorized")
            .slice(0, 6);

          const allCats = parents.map((parent) => ({
            ...parent,
            children: data.filter((c) => c.parent_id === parent.id),
          }));
          setCategories(allCats);
        }
      })
      .catch(console.error)
      .finally(() => setCategoriesLoading(false));
  }, []);

  // Fetch products
  useEffect(() => {
    fetch(`${API.base}/products.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ action: "fetch" }),
    })
      .then((res) => res.json())
      .then((data) =>
        setAllProducts(Array.isArray(data) ? data : data?.data || [])
      )
      .catch(console.error);
  }, []);

  // Suggestions toggle
  useEffect(
    () => setSuggestionsOpen(normalizedSearch.length >= 2),
    [normalizedSearch]
  );

  useEffect(() => {
    if (!suggestionsOpen) return;
    const handleOutside = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target))
        setSuggestionsOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [suggestionsOpen]);

  // Auth storage updates
  useEffect(() => {
    const onStorage = () => {
      setAuthToken(localStorage.getItem("authToken"));
      setUser(readStoredUser());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("userUpdated", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("userUpdated", onStorage);
    };
  }, []);

  // Drawer outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        drawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(e.target)
      ) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [drawerOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) return RenderToast({ type: "error", message: "Please enter SKU" });

    // const product = allProducts.find(
    //   (p) => p.sku?.toLowerCase() === q || p.productName?.toLowerCase() === q
    // );
    // if (product) {
    //   navigate(`/product/${product.product_slug}`);
    //   setSearch("");
    //   setSuggestionsOpen(false);
    // } else RenderToast({ type: "error", message: "Product not found" });

    setSearchLoading(true);
    setSuggestionsOpen(true);

    setTimeout(() => {
      const product = allProducts.find(
        (p) => p.sku?.toLowerCase() === q || p.productName?.toLowerCase() === q
      );

      setSearchLoading(false);

      if (product) {
        navigate(`/product/${product.product_slug}`);
        setSearch("");
        setSuggestionsOpen(false);
      } else {
        RenderToast({ type: "error", message: "Product not found" });
      }
    }, 500);
  };

  const handleSuggestionSelect = (product) => {
    navigate(`/product/${product.product_slug || product.sku}`);
    setSearch("");
    setSuggestionsOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("userUpdated"));
    setAuthToken(null);
    setUser(null);
    setDashOpen(false);
    navigate("/");
  };

  const allCatRef = useRef();

  return (
    <header className={classes.header}>
      {/* Top Row: Logo + Search + Auth */}
      <div className={`${classes.rowFull} ${classes.topRow}`}>
        <div className={classes.rowInner}>
          <div className={classes.logoWrap}>
            <Link to="/" className={classes.logoLink}>
              <img
                src="/serverstock_uk_white_logo.png"
                alt="Server Stock"
                className={classes.logoImg}
              />
            </Link>
          </div>

          {/* Search */}
          <div className={classes.searchWrap} ref={searchWrapRef}>
            <div className={classes.searchInner}>
              <form
                onSubmit={handleSearchSubmit}
                className={classes.searchForm}
              >
                {/* Categories Dropdown */}
                <div
                  className={classes.searchCategoriesWrap}
                  ref={searchCategoriesRef}
                >
                  <button
                    type="button"
                    className={classes.searchCategoriesBtn}
                    onClick={() =>
                      setOpenMenus((prev) => ({
                        ...prev,
                        searchCategories: !prev.searchCategories,
                      }))
                    }
                  >
                    All Categories{" "}
                    <FaChevronDown className={classes.chevDown} />
                  </button>
                  {openMenus.searchCategories && (
                    <ul className={classes.searchCategoriesDropdown}>
                      {categoriesLoading
                        ? Array.from({ length: 6 }).map((_, idx) => (
                            <li key={idx} className={classes.catSkeleton}></li>
                          ))
                        : categories.map((cat) => (
                            <li key={cat.id}>
                              <NavLink
                                to={`/category/${cat.category_slug}`}
                                className={classes.catLink}
                                onClick={() => setOpenMenus({})}
                              >
                                <FaBox className={classes.catIcon} />
                                {cat.categoryName}
                              </NavLink>
                            </li>
                          ))}
                    </ul>
                  )}
                </div>

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search product SKU..."
                  className={classes.searchInput}
                  onFocus={() =>
                    normalizedSearch.length >= 2 && setSuggestionsOpen(true)
                  }
                />
                <button type="submit" className={classes.searchBtn}>
                  <FaSearch />
                </button>
              </form>

              {/* {suggestionsOpen && (
                <div className={classes.searchDropdown}>
                  {suggestionResults.length === 0 ? (
                    <p className={classes.searchDropdownEmpty}>
                      No matching products
                    </p>
                  ) : (
                    suggestionResults.map((product) => (
                      <button
                        type="button"
                        key={product.id ?? product.sku}
                        className={classes.searchDropdownItem}
                        onClick={() => handleSuggestionSelect(product)}
                      >
                        <img
                          src={getProductThumb(product.productImage)}
                          alt={product.productName}
                          className={classes.searchDropdownImage}
                          onError={(e) =>
                            (e.currentTarget.src = "/placeholder-image.png")
                          }
                        />
                        <div className={classes.searchDropdownMeta}>
                          <p className={classes.searchDropdownTitle}>
                            {product.productName || "Untitled Product"}
                          </p>
                          <span className={classes.searchDropdownSku}>
                            SKU: {product.sku || "N/A"}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )} */}

              {suggestionsOpen && (
                <div className={classes.searchDropdown}>
                  {searchLoading ? (
                    <p className={classes.searchDropdownEmpty}>
                      Searching product…
                    </p>
                  ) : suggestionResults.length === 0 ? (
                    <p className={classes.searchDropdownEmpty}>
                      No matching products
                    </p>
                  ) : (
                    suggestionResults.map((product) => (
                      <button
                        type="button"
                        key={product.id ?? product.sku}
                        className={classes.searchDropdownItem}
                        onClick={() => handleSuggestionSelect(product)}
                      >
                        <img
                          src={getProductThumb(product.productImage)}
                          alt={product.productName}
                          className={classes.searchDropdownImage}
                          onError={(e) =>
                            (e.currentTarget.src = "/placeholder-image.png")
                          }
                        />
                        <div className={classes.searchDropdownMeta}>
                          <p className={classes.searchDropdownTitle}>
                            {product.productName || "Untitled Product"}
                          </p>
                          <span className={classes.searchDropdownSku}>
                            SKU: {product.sku || "N/A"}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Auth */}
          <div className={classes.authWrap}>
            <div className={classes.headerSupportBtnContainer}>
              <div className={classes.headerSupportBtnIcon}>
                <BiSupport />
              </div>
              <div className={classes.headerSupportBtnText}>
                <h5>Need Any Help?</h5>
                <Link to="tel:+442475420877">
                  <h3>+44 247 542 0877</h3>
                </Link>
              </div>
            </div>
            {/* {authToken ? (
              <div className={classes.dashboard}>
                <button
                  className={classes.authBtn}
                  onClick={() => setDashOpen((s) => !s)}
                >
                  <FaUserCircle className={classes.iconSmall} />
                  {user?.name || "Dashboard"}{" "}
                  <FaChevronDown className={classes.chev} />
                </button>
                {dashOpen && (
                  <div className={classes.dashDropdown}>
                    <Link
                      to="/dashboard"
                      className={classes.dropdownItem}
                      onClick={() => setDashOpen(false)}
                    >
                      <FaUserCog /> Dashboard
                    </Link>
                    <button
                      className={classes.dropdownItem}
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={classes.userAuthButtonContainer}>
                <div className={classes.userAuthButtonIcon}>
                  <GrContact />
                </div>
                <div
                  className={classes.userAuthButtonText}
                  onClick={() => navigate("/contact")}
                >
                  <h5>Get a</h5>
                  <h3>Quote</h3>
                </div>
              </div>
            )} */}
            <div className={classes.userAuthButtonContainer}>
              <div className={classes.userAuthButtonIcon}>
                <GrContact />
              </div>
              <div
                className={classes.userAuthButtonText}
                onClick={() => navigate("/contact")}
              >
                <h5>Get a</h5>
                <h3>Quote</h3>
              </div>
            </div>

            <button
              className={classes.hamburger}
              onClick={() => setDrawerOpen(true)}
            >
              <FaBars />
            </button>
          </div>
        </div>
      </div>

      {/* Browse All Category */}
      <div className={classes.categoriesRow}>
        <div className={classes.allCategoriesSection} ref={allCatRef}>
          {/* All Categories Button */}
          <div className={classes.allCategoriesItemSection}>
            <div className={classes.allCategoriesItemsContentList1}>
              <div
                className={classes.allCategoriesContent}
                onMouseEnter={() => setOpenAllCategories(true)}
                onMouseLeave={() => setOpenAllCategories(false)}
              >
                <RxHamburgerMenu />
                <h2>Browse All Category</h2>

                {/* Categories Wrapper */}
                {openAllCategories && (
                  <div className={classes.categoriesWrapper}>
                    <ul className={classes.parentCategories}>
                      {categoriesLoading
                        ? Array.from({ length: 6 }).map((_, idx) => (
                            <li key={idx} className={classes.catSkeleton}></li>
                          ))
                        : categories.map((cat) => (
                            <li
                              key={cat.id}
                              className={classes.parentCatItem}
                              onMouseEnter={() => setOpenParent(cat.id)}
                              onMouseLeave={() => setOpenParent(null)}
                            >
                              <NavLink
                                to={`/category/${cat.category_slug}`}
                                className={classes.parentCatLink}
                              >
                                <div className={classes.catWrap}>
                                  <div className={classes.catTitle}>
                                    <FaBox className={classes.catIcon} />
                                    <h2>{cat.categoryName}</h2>
                                  </div>

                                  <div className={classes.chevWrap}>
                                    {cat.children.length > 0 && (
                                      <IoIosArrowForward
                                        className={`${classes.chevSmall} ${
                                          openParent === cat.id
                                            ? classes.rotate
                                            : ""
                                        }`}
                                      />
                                    )}
                                  </div>
                                </div>
                              </NavLink>

                              {/* Mega Menu */}
                              {/* {cat.children.length > 0 && (
                                <div
                                  className={`${classes.megaMenu} ${
                                    openParent === cat.id
                                      ? classes.megaVisible
                                      : ""
                                  }`}
                                >
                                  {cat.children.map((child) => (
                                    <Link
                                      key={child.id}
                                      to={`/category/${child.category_slug}`}
                                      className={classes.megaCard}
                                    >
                                      <FaBox className={classes.megaIcon} />
                                      <div className={classes.megaTitle}>
                                        {child.categoryName}
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              )} */}

                              {cat.children.length > 0 && (
                                <div
                                  className={`${classes.megaMenu} ${
                                    openParent === cat.id
                                      ? classes.megaVisible
                                      : ""
                                  } ${
                                    cat.children.length === 1
                                      ? classes.oneColumn
                                      : ""
                                  }`}
                                >
                                  {cat.children.map((child) => (
                                    <Link
                                      key={child.id}
                                      to={`/category/${child.category_slug}`}
                                      className={classes.megaCard}
                                    >
                                      <FaBox className={classes.megaIcon} />
                                      <div className={classes.megaTitle}>
                                        {child.categoryName}
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </li>
                          ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Nav Links */}
              <div className={classes.homeLinks}>
                <Link to={"/"}>Home</Link>
                <Link to={"/about"}>About</Link>
                <Link to={"/shop"}>Shop</Link>
                <Link to={"/contact"}>Contact</Link>
              </div>
            </div>
            <div className={classes.allCategoriesItemsContentList2}>
              <div className={classes.supportContainer}>
                <div className={classes.supportContainerIcon}>
                  <img src="./header-icons/support.svg" />
                </div>
                <div className={classes.supportContainerContent}>
                  <h5>Support Online</h5>
                  <h3>We support customers 24/7</h3>
                </div>
              </div>
              <div className={classes.supportContainer}>
                <div className={classes.supportContainerIcon}>
                  <img src="./header-icons/bestPrice.svg" />
                </div>
                <div className={classes.supportContainerContent}>
                  <h5>Best Prices</h5>
                  <h3>Unbeatable Value</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`${classes.drawerOverlay} ${
          drawerOpen ? classes.drawerOpen : ""
        }`}
      >
        <div ref={drawerRef} className={classes.drawer}>
          <button
            className={classes.drawerClose}
            onClick={() => setDrawerOpen(false)}
          >
            <FaTimes />
          </button>
          <div className={classes.drawerLogo}>
            <img src="/serverstock_uk_color_logo.png" alt="Server Stock Logo" />
          </div>
          <nav className={classes.drawerLinks}>
            <Link
              to="/"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/shop"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Shop
            </Link>
            <Link
              to="/about"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              About
            </Link>
            <Link
              to="/contact"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Contact
            </Link>
          </nav>
          <div className={classes.drawerDivider}></div>
          {/* {authToken ? (
            <>
              <button
                className={classes.drawerItem}
                onClick={() => {
                  navigate("/dashboard");
                  setDrawerOpen(false);
                }}
              >
                <FaUserCog /> Dashboard
              </button>
              <button className={classes.drawerItem} onClick={handleLogout}>
                <FaSignOutAlt /> Logout
              </button>
            </>
          ) : (
            <button
              className={classes.drawerItem}
              onClick={() => {
                navigate("/server-login-584"); // Redirect to login page
                setDrawerOpen(false);
              }}
            >
              <FaSignInAlt /> Login
            </button>
          )} */}
          <div className={classes.drawerBottom}>
            <SocialSection />
          </div>
        </div>
      </div>
    </header>
  );
}
